import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, Trash2, Mail, Phone, Loader2, Inbox, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) { logout(); navigate("/login"); }
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user) load();
  }, [user, load, navigate]);

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await axios.delete(`${API}/leads/${id}`, auth);
    setLeads((l) => l.filter((x) => x.id !== id));
  };

  const doLogout = () => { logout(); navigate("/login"); };

  const promoCount = leads.filter((l) => l.promo_interest).length;

  if (user === null) {
    return (
      <div className="min-h-screen bg-navy-deep flex items-center justify-center text-gold">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-deep grain topo text-cream">
      <header className="border-b border-gold/20 bg-navy-950/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="mx-auto max-w-[1300px] px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/crane_light.png" alt="" className="h-10 w-10 object-contain" />
            <div className="leading-none">
              <p className="font-display text-lg tracking-wide">KINGDOM ADVANCEMENT</p>
              <p className="text-[9px] text-gold tracking-luxe mt-0.5">LEADS DASHBOARD</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="inline-flex items-center gap-2 border border-gold/50 text-gold text-xs uppercase tracking-[0.15em] px-5 py-2.5 rounded-full hover:bg-gold hover:text-navy-deep transition-colors duration-300"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-gold text-xs uppercase tracking-luxe mb-2">Welcome back, {user.name || "Admin"}</p>
            <h1 className="font-display text-4xl md:text-5xl">Your Inquiries</h1>
          </div>
          <div className="flex gap-4">
            <div className="border border-gold/25 rounded-xl px-6 py-4 bg-white/[0.03]">
              <p className="font-display text-3xl text-gold">{leads.length}</p>
              <p className="text-[10px] uppercase tracking-luxe text-cream/60 mt-1">Total Leads</p>
            </div>
            <div className="border border-gold/25 rounded-xl px-6 py-4 bg-white/[0.03]">
              <p className="font-display text-3xl text-gold">{promoCount}</p>
              <p className="text-[10px] uppercase tracking-luxe text-cream/60 mt-1">$50 Offer</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24 text-gold"><Loader2 className="animate-spin" /></div>
        ) : leads.length === 0 ? (
          <div className="text-center py-24 text-cream/50" data-testid="no-leads">
            <Inbox className="mx-auto mb-4" />
            <p className="font-display text-2xl">No inquiries yet.</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="leads-list">
            {leads.map((l, i) => (
              <motion.div
                key={l.id}
                data-testid="lead-row"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.4) }}
                className="border border-gold/20 rounded-xl p-5 md:p-6 bg-white/[0.03] hover:border-gold/50 transition-colors duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                  <div className="md:w-56 shrink-0">
                    <div className="flex items-center gap-2">
                      <p className="font-display text-2xl text-cream">{l.name}</p>
                      {l.promo_interest && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wide bg-gold text-navy-deep px-2 py-0.5 rounded-full">
                          <Sparkles size={10} /> $50
                        </span>
                      )}
                    </div>
                    {l.business && <p className="text-cream/50 text-sm mt-1">{l.business}</p>}
                    <p className="text-gold/80 text-xs uppercase tracking-wide mt-2">{l.category}</p>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-cream/70">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors"><Mail size={13} /> {l.email}</a>
                      {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors"><Phone size={13} /> {l.phone}</a>}
                    </div>
                    {l.message && <p className="text-cream/60 text-sm mt-2 italic">"{l.message}"</p>}
                    <p className="text-cream/30 text-xs mt-2">{new Date(l.created_at).toLocaleString()}</p>
                  </div>

                  <button
                    data-testid={`delete-lead-${l.id}`}
                    onClick={() => remove(l.id)}
                    aria-label="Delete lead"
                    className="shrink-0 self-start md:self-center h-10 w-10 rounded-full border border-red-400/30 text-red-300/80 flex items-center justify-center hover:bg-red-400/20 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
